import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../style/speakSearch.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BulkSlipPrintApp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // --- Get App ID from Query String ---
    const queryParams = new URLSearchParams(location.search);
    const initialAppId = queryParams.get('app_id') || "";
    
    const lang = 'en';

    // --- State ---
    const [currentAppId, setCurrentAppId] = useState(initialAppId);
    const [booths, setBooths] = useState([]);
    const [selectedBooth, setSelectedBooth] = useState("");
    const [voters, setVoters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingBooths, setFetchingBooths] = useState(false);
    const [showActionButtons, setShowActionButtons] = useState(false);
    const [isSamplePrinting, setIsSamplePrinting] = useState(false);
    const [sampleVoters, setSampleVoters] = useState([]);
    const printRef = useRef();

    

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Bulk Slip Print",
            selectVidhanSabha: "Select Vidhan Sabha",
            selectBooth: "Select Booth Number",
            btnGenerate: "Generate All Slips",  
            btnExportPDF: "Print / Export PDF",
            loadingBooths: "Loading Booths...",
            loadingData: "Fetching Voters...",
            noVoters: "No voters found for this booth.",
            btnSample: "Sample View (Print 10 Slips)"
        },
        hi: {
            pageTitle: "बल्क स्लिप प्रिंट",
            selectVidhanSabha: "विधानसभा चुनें",
            selectBooth: "बूथ नंबर चुनें",
            btnGenerate: "सभी स्लिप बनाएं",
            btnExportPDF: "प्रिंट / पीडीएफ डाउनलोड करें",
            loadingBooths: "बूथ लोड हो रहे हैं...",
            loadingData: "मतदाता लाए जा रहे हैं...",
            noVoters: "इस बूथ के लिए कोई मतदाता नहीं मिला।",
            btnSample: "नमूना देखें (10 पर्ची प्रिंट करें)"
        },
        gu: {
            pageTitle: "બલ્ક સ્લિપ પ્રિન્ટ",
            selectVidhanSabha: "વિધાનસભા પસંદ કરો",
            selectBooth: "બૂથ નંબર પસંદ કરો",
            btnGenerate: "બધા સ્લિપ બનાવો",
            btnExportPDF: "પ્રિન્ટ / PDF ડાઉનલોડ કરો",
            loadingBooths: "બૂથ લોડ થઈ રહ્યા છે...",
            loadingData: "મતદારો લાવવામાં આવી રહ્યા છે...",
            noVoters: "આ બૂથ માટે કોઈ મતદાર મળ્યો નથી.",
            btnSample: "સેમ્પલ જુઓ (૧૦ સ્લિપ પ્રિન્ટ કરો)"
        }
    };

    const txt = t[lang] || t.en;

    // --- Generate 1 to 24 Vidhan Sabha Array ---
    const vidhanSabhas = Array.from({ length: 24 }, (_, i) => i + 1);

    // --- 1. Fetch Booths whenever currentAppId changes ---
    useEffect(() => {
        if (!currentAppId) {
            setBooths([]);
            setSelectedBooth("");
            setShowActionButtons(false);
            setVoters([]);
            return;
        }

        const fetchBooths = async () => {
            setFetchingBooths(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_total_booth`, {
                    app_id: currentAppId
                });
                
                if (response.data.success && response.data.data && response.data.data.result) {
                    setBooths(response.data.data.result);
                } else {
                    setBooths([]);
                }
            } catch (error) {
                console.error("Error fetching booths:", error);
                setBooths([]);
            } finally {
                setFetchingBooths(false);
            }
        };

        fetchBooths();
        setSelectedBooth("");
        setVoters([]);
        setShowActionButtons(false);
    }, [currentAppId]);

    // --- Handle Vidhan Sabha Change ---
    const handleVidhanSabhaChange = (e) => {
        setCurrentAppId(e.target.value);
    };

    // --- Generate Full Voter List ---
    const handleGenerate = async () => {
        if (!currentAppId) {
            alert(txt.selectVidhanSabha);
            return;
        }
        if (!selectedBooth) {
            alert(txt.selectBooth);
            return;
        }

        setLoading(true);
        setShowActionButtons(false);

        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_search`, {
                app_id: currentAppId,
                booth_no: selectedBooth
            });

            if (response.data.success) {
                const fetchedVoters = response.data.data.result || [];
                setVoters(fetchedVoters);
                setShowActionButtons(true);
            } else {
                setVoters([]);
                setShowActionButtons(false);
            }
        } catch (error) {
            console.error("Error fetching voters:", error);
            setVoters([]);
            setShowActionButtons(false);
        } finally {
            setLoading(false);
        }
    };

    // --- Handle Sample Print (Only First 10 Slips) - Does NOT change main display ---
    const handleSamplePrint = async () => {
        if (!currentAppId || !selectedBooth) return;
        
        setIsSamplePrinting(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_search`, {
                app_id: currentAppId,
                booth_no: selectedBooth
            });

            if (response.data.success) {
                const allVoters = response.data.data.result || [];
                const sampleData = allVoters.slice(0, 10);
                setSampleVoters(sampleData);
                
                setTimeout(() => {
                    window.print();
                }, 300);
            }
        } catch (error) {
            console.error("Error fetching voters for sample:", error);
            alert("Failed to load sample data");
        } finally {
            // Clear sample voters after print dialog closes (with a delay)
            setTimeout(() => {
                setIsSamplePrinting(false);
                setSampleVoters([]);
            }, 1000);
        }
    };
    
    // --- Handle PDF Export / Print (Full List) ---
    const handleExportPDF = () => {
        window.print();
    };

    const isGenerateDisabled = loading || !selectedBooth || !currentAppId;

    // Determine which voters to display (main list or sample for print)
    const displayVoters = isSamplePrinting ? sampleVoters : voters;
    const shouldShowPrintArea = (!loading && displayVoters.length > 0) || isSamplePrinting;

    return (
        <div className='app-container p-4'>
            <div className="no-print">
                
                {/* --- FILTER BAR --- */}
                <div className="controls-row mb-4 slide-up-element" style={{ animationDelay: '0.1s' , position: 'sticky', top: '16px', zIndex: 10 }}>
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                        
                        {/* 1. Vidhan Sabha Dropdown */}
                        <div className="search-wrapper-modern flex-grow-1" style={{ maxWidth: '250px' }}>
                            <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <select 
                                className="search-input-modern w-100" 
                                value={currentAppId}
                                onChange={handleVidhanSabhaChange}
                                style={{ appearance: 'auto' }}
                            >
                                <option value="">-- {txt.selectVidhanSabha} --</option>
                                {vidhanSabhas.map(num => (
                                    <option key={num} value={num.toString()}>
                                        Vidhan Sabha {num}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* 2. Booth Number Dropdown */}
                        <div className="search-wrapper-modern flex-grow-1" style={{ maxWidth: '250px' }}>
                            <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <select 
                                className="search-input-modern w-100" 
                                value={selectedBooth}
                                onChange={(e) => {
                                    setSelectedBooth(e.target.value);
                                    setShowActionButtons(false);
                                    setVoters([]);
                                }}
                                disabled={fetchingBooths || !currentAppId}
                                style={{ appearance: 'auto' }}
                            >
                                <option value="">-- {fetchingBooths ? txt.loadingBooths : txt.selectBooth} --</option>
                                {!fetchingBooths && booths.map((b, i) => (
                                    <option key={i} value={b.booth_no}>
                                        Booth No: {b.booth_no} {b.total_voter ? `(${b.total_voter} Voters)` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Generate All Slips Button */}
                        <button 
                            className="btn-search-main" 
                            onClick={handleGenerate}
                            disabled={isGenerateDisabled}
                            style={{ background: '#2563eb', padding: '12px 24px', borderRadius: '12px', color: 'white', fontWeight: '600', border: 'none' }}
                        >
                            <div className="d-flex align-items-center gap-2">
                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                {txt.btnGenerate}
                            </div>
                        </button>

                        {/* Action Buttons - Show only after Generate is clicked */}
                        {showActionButtons && voters.length > 0 && (
                            <>
                                {/* Sample View Button */}
                                <button 
                                    className="btn-sample-view"
                                    onClick={handleSamplePrint}
                                    disabled={loading || isSamplePrinting}
                                    style={{ background: '#10b981', padding: '12px 24px', borderRadius: '12px', color: 'white', fontWeight: '600', border: 'none' }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                        {txt.btnSample}
                                    </div>
                                </button>

                                {/* Export PDF Button */}
                                <button 
                                    className="btn-print-bulk ms-auto"
                                    onClick={handleExportPDF}
                                    style={{ background: '#f97316', padding: '12px 24px', borderRadius: '12px', color: 'white', fontWeight: '600', border: 'none' }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                        {txt.btnExportPDF}
                                    </div>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {loading && (
                <div className="loader-wrapper no-print">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {!selectedBooth && !loading && !showActionButtons && (
                <div className="text-center text-muted p-5 modern-card mt-3 no-print">
                    <h4>{txt.selectBooth}</h4>
                </div>
            )}

            {selectedBooth && !loading && voters.length === 0 && showActionButtons && !isSamplePrinting && (
                <div className="text-center text-muted p-5 modern-card mt-3 no-print">
                    <h4>{txt.noVoters}</h4>
                </div>
            )}

            {/* --- PRINT AREA - Shows either full list OR sample list during printing --- */}
            <div id="printable-a4-area" className="screen-preview">
                {shouldShowPrintArea && (
                    <div className="continuous-a4-sheet">
                        <div className="slip-grid-dynamic">
                            {displayVoters.map((voter, index) => (
                                <div className="voter-card-print" key={`${voter.id || index}-${isSamplePrinting ? 'sample' : 'full'}`}>
                                    <div className="slip-container" style={{ border: '2px solid #000', backgroundColor: '#fff', color: '#000', height: "fit-content", display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                                        <div style={{ flex: 'none', width: '100%', borderBottom: '2px solid #000', backgroundColor: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                                            <img src="http://rajkotcorporation.bjpwings.com/img/prachar/19042026030352_1.jpg" alt="Slip Banner" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                        </div>
                                        <div style={{ flex: 'none', padding: '8px', fontSize: '13px', lineHeight: '1.6', fontWeight: '600' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap' }}>
                                                <span>ભાગ નં - {voter.part_no || "-"}</span>
                                                <span>બૂથ નં - {voter.booth_no || voter.part_no || "-"}</span>
                                                <span>ક્રમ નં. - {voter.serial_no || voter.slnoinpart || (index + 1)}</span>
                                            </div>
                                            <div style={{ marginBottom: '3px' }}>નામ : {voter.eng_f_name} {voter.eng_surname}</div>
                                            <div style={{ marginBottom: '3px' }}>જાતિ/ઉંમર : {voter.sex_age || voter.sex || "-"}</div>
                                            <div style={{ marginBottom: '3px' }}>આઈડીકાર્ડ : {voter.idcard_no || "-"}</div>
                                            <div>મતદાન સ્થળ : {voter.eng_polling_location || "-"}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding-bottom: 80px; }
                
                .controls-row { background: #fff; padding: 16px 20px; border-radius: 16px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); border: 1px solid #f1f5f9; }
                
                .search-wrapper-modern { position: relative; }
                .search-icon-modern { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 18px; height: 18px; pointer-events: none;}
                .search-input-modern { width: 100%; padding: 14px 16px 14px 46px; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; color: #1e293b; background-color: #f8fafc; outline: none; transition: all 0.2s ease; cursor: pointer; }
                .search-input-modern:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); background-color: white;}
                
                .modern-card { background: #FFFFFF; border-radius: 16px; box-shadow: 0 4px 12px rgba(0,0,0,0.05); border: 1px solid #E2E8F0; }

                .btn-search-main:disabled { background: #94a3b8 !important; cursor: not-allowed; }
                .btn-sample-view:disabled { background: #94a3b8 !important; cursor: not-allowed; }
                .btn-print-bulk { transition: all 0.2s ease; }
                .btn-print-bulk:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(249, 115, 22, 0.3); }
                .btn-sample-view:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3); }

                /* --- DYNAMIC CONTINUOUS A4 SHEET DESIGN --- */
                .screen-preview {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .continuous-a4-sheet {
                    width: 210mm;
                    padding: 10mm;
                    background: white;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
                }

                /* Using flex guarantees perfect row breaking */
                .slip-grid-dynamic {
                    display: flex; 
                    flex-wrap: wrap;
                    align-content: flex-start;
                    gap: 10mm;
                    width: 100%;
                }

                .voter-card-print {
                    width: calc(50% - 5mm);
                    height: fit-content;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }

                /* Responsive Screen Scaling */
                @media screen and (max-width: 820px) {
                    .continuous-a4-sheet { transform: scale(0.8); transform-origin: top center; margin-bottom: -10%; }
                }
                @media screen and (max-width: 640px) {
                    .continuous-a4-sheet { transform: scale(0.6); transform-origin: top center; margin-bottom: -30%; }
                }
                @media screen and (max-width: 480px) {
                    .continuous-a4-sheet { transform: scale(0.43); transform-origin: top center; margin-bottom: -50%; }
                }
                @media screen and (max-width: 380px) {
                    .continuous-a4-sheet { transform: scale(0.36); transform-origin: top center; margin-bottom: -60%; }
                }

                /* --- STRICT PRINT STYLES --- */
                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 10mm 0mm 0mm 0mm !important; 
                    }
                    body, html {
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    body * {
                        visibility: hidden;
                    }
                    .app-container {
                        padding: 0 !important;
                        margin: 0 !important;
                        background: transparent !important;
                    }
                    .screen-preview, .screen-preview * {
                        visibility: visible;
                    }
                    .screen-preview {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .continuous-a4-sheet {
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 10mm !important; 
                        box-shadow: none !important;
                        transform: none !important;
                    }
                    .slip-grid-dynamic {
                        display: flex !important;
                        flex-wrap: wrap !important;
                        align-content: flex-start !important;
                        gap: 10mm !important;
                        width: 100% !important;
                    }
                    .voter-card-print {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                    * {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                }

                /* --- LOADER --- */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 40vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                .loader-text { color: #6B7280; font-weight: 600; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }

                @media (max-width: 768px) {
                    .controls-row .d-flex {align-items: stretch !important; }
                    .search-wrapper-modern { max-width: 100% !important; }
                    .btn-print-bulk { margin-left: 0 !important; width: 100%; justify-content: center; display: flex; }
                    .btn-search-main { width: 100%; justify-content: center; display: flex; }
                    .btn-sample-view { width: 100%; justify-content: center; display: flex; }
                }
            `}</style>
        </div>
    );
};

export default BulkSlipPrintApp;